/*
 * Licensed Materials - Property of IBM
 *
 * PID 5725-H26
 *
 * Copyright IBM Corporation 2018. All Rights Reserved.
 *
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import { ReduxUtils } from '@spm/core';
import { combineReducers } from 'redux';
import * as ACTIONTYPES from './actionTypes';

/**
 * Updates the LifeEvents Form details of the 'UALifeEvents' store object.
 *
 * @param {any} [state={}] the initial state for the reducer
 * @param {any} action the action creator
 * @returns the new state
 */
function createLifeEventsFormReducer() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var action = arguments.length > 1 ? arguments[1] : undefined;
  return ReduxUtils.generateReducer(state, action, ACTIONTYPES.START_LIFE_EVENTS_FORM);
}

/**
 * Updates the indicator of creation of LifeEvents Form details of the 'UALifeEvents' store object.
 *
 * @param {any} [state={}] the initial state for the reducer
 * @param {any} action the action creator
 * @returns the new state
 */
function isCreatingLifeEventsFormReducer() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var action = arguments.length > 1 ? arguments[1] : undefined;
  return ReduxUtils.generateFetchingReducer(state, action, ACTIONTYPES.START_LIFE_EVENTS_FORM);
}

/**
 * Updates the post data errors for the create LifeEvents form.
 *
 * @param {*} state
 * @param {*} action
 */
function createLifeEventsFormErrorReducer() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var action = arguments.length > 1 ? arguments[1] : undefined;
  return ReduxUtils.generateErrorReducer(state, action, ACTIONTYPES.START_LIFE_EVENTS_FORM);
}

/**
 * Updates the indicator of submission of Life Event form of the 'UALifeEvents' store object.
 *
 * @param {any} [state={}] the initial state for the reducer
 * @param {any} action the action creator
 * @returns the new state
 */
function isFinishingLifeEventsFormReducer() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var action = arguments.length > 1 ? arguments[1] : undefined;
  return ReduxUtils.generateFetchingReducer(state, action, ACTIONTYPES.FINISH_LIFE_EVENT_FORM_TYPE);
}

/**
 * Updates the post data errors for the submission of a Life Events form.
 *
 * @param {*} state
 * @param {*} action
 */
function finishLifeEventsFormErrorReducer() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var action = arguments.length > 1 ? arguments[1] : undefined;
  return ReduxUtils.generateErrorReducer(state, action, ACTIONTYPES.FINISH_LIFE_EVENT_FORM_TYPE);
}

/**
 * Updates the LifeEvents data within the Redux store.
 *
 * @param {any} [state={}] the initial state for the reducer
 * @param {any} action the action creator
 * @returns the new state
 */
function lifeEventsSelectionReducer() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var action = arguments.length > 1 ? arguments[1] : undefined;
  return ReduxUtils.generateReducer(state, action, ACTIONTYPES.SET_LIFE_EVENTS_SELECTION);
}

/**
 * Updates the flag that is set while the get data request is in progress for the LifeEvents confirmation
 * page.
 *
 * @param {any} [state={}] the initial state for the reducer
 * @param {any} action the action creator
 * @returns the new state
 */
function isFetchingLifeEventsSelectionReducer() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var action = arguments.length > 1 ? arguments[1] : undefined;
  return ReduxUtils.generateFetchingReducer(state, action, ACTIONTYPES.SET_LIFE_EVENTS_SELECTION);
}

/**
 * Updates the get data errors for the confirmation page.
 *
 * @param {any} [state={}] the initial state for the reducer
 * @param {any} action the action creator
 * @returns the new state
 */
function lifeEventsSelectionErrorReducer() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var action = arguments.length > 1 ? arguments[1] : undefined;
  return ReduxUtils.generateErrorReducer(state, action, ACTIONTYPES.SET_LIFE_EVENTS_SELECTION);
}

/**
 * Life Events History Reducer.
 *
 * @param {any} [state={}] the initial state for the reducer
 * @param {any} action the action creator
 * @returns the new state
 */
var lifeEventsHistoryReducer = function lifeEventsHistoryReducer() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var action = arguments.length > 1 ? arguments[1] : undefined;
  return ReduxUtils.generateReducer(state, action, ACTIONTYPES.LIFE_EVENT_HISTORY);
};

/**
 * Life Events History Error Reducer.
 *
 * @param {any} [state={}] the initial state for the reducer
 * @param {any} action the action creator
 * @returns the new state
 */
var lifeEventsHistoryErrorReducer = function lifeEventsHistoryErrorReducer() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var action = arguments.length > 1 ? arguments[1] : undefined;
  return ReduxUtils.generateErrorReducer(state, action, ACTIONTYPES.LIFE_EVENT_HISTORY);
};

/**
 * List of Life Event Remote Systems.
 *
 * @param {any} [state={}] the initial state for the reducer
 * @param {any} action the action creator
 * @returns the new state
 */
function listLifeEventRemoteSystemsReducer() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var action = arguments.length > 1 ? arguments[1] : undefined;
  return ReduxUtils.generateReducer(state, action, ACTIONTYPES.LIFE_EVENT_LIST_REMOTE_SYSTEMS);
}

/**
 * Updates the flag that is set while we are getting the life of Life Event Remote Systems.
 *
 * @param {any} [state={}] the initial state for the reducer
 * @param {any} action the action creator
 * @returns the new state
 */
function isFetchingListLifeEventRemoteSystemsReducer() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var action = arguments.length > 1 ? arguments[1] : undefined;
  return ReduxUtils.generateFetchingReducer(state, action, ACTIONTYPES.LIFE_EVENT_LIST_REMOTE_SYSTEMS);
}

/**
 * Updates the error object with any error that happened while getting the list of Life Event Remote
 * Systems.
 *
 * @param {any} [state={}] the initial state for the reducer
 * @param {any} action the action creator
 * @returns the new state
 */
function listLifeEventRemoteSystemsErrorReducer() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var action = arguments.length > 1 ? arguments[1] : undefined;
  return ReduxUtils.generateErrorReducer(state, action, ACTIONTYPES.LIFE_EVENT_LIST_REMOTE_SYSTEMS);
}

/**
 * Updates the flag that is set while we are posting the request to send the given life event to the
 * selected Remote Systems.
 *
 * @param {Object} state The initial state for the reducer.
 * @param {function} action The specified action creator.
 * @returns a boolean to indicate if 'goToPage' is being posted.
 */
function isPostingSendLifeEventToRemoteSystemsReducer() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var action = arguments.length > 1 ? arguments[1] : undefined;
  return ReduxUtils.generateFetchingReducer(state, action, ACTIONTYPES.LIFE_EVENT_SEND_TO_REMOTE_SYSTEMS);
}

/**
 * Updates the error object with any error that happened while posting the request to send the given
 * life event to the selected Remote Systems.
 *
 * @param {Object} state The initial state for the reducer.
 * @param {function} action The specified action creator.
 * @returns Any error messages encountered when moving the script to the first page.
 */
function sendLifeEventToRemoteSystemsErrorReducer() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var action = arguments.length > 1 ? arguments[1] : undefined;
  return ReduxUtils.generateErrorReducer(state, action, ACTIONTYPES.LIFE_EVENT_SEND_TO_REMOTE_SYSTEMS);
}

/**
 * Gets the selected Remote Systems.
 *
 * @param {any} [state={}] the initial state for the reducer
 * @param {any} action the action creator
 * @returns the new state
 */
function getSelectedLifeEventRemoteSystemsReducer() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var action = arguments.length > 1 ? arguments[1] : undefined;
  return ReduxUtils.generateReducer(state, action, ACTIONTYPES.LIFE_EVENT_SET_SELECTED_REMOTE_SYSTEMS);
}

/**
 * Gets the data on whether to show the Remote Systems validation error message.
 *
 * @param {any} [state={}] the initial state for the reducer
 * @param {any} action the action creator
 * @returns the new state
 */
function showRemoteSystemsValidationMessageReducer() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var action = arguments.length > 1 ? arguments[1] : undefined;
  return ReduxUtils.generateReducer(state, action, ACTIONTYPES.LIFE_EVENT_SHOW_REMOTE_SYSTEMS_VALIDATION_MESSAGE);
}
var reducers = combineReducers({
  createLifeEventsForm: createLifeEventsFormReducer,
  isFetchingLifeEventsSelection: isFetchingLifeEventsSelectionReducer,
  lifeEventsSelectionError: lifeEventsSelectionErrorReducer,
  isCreatingLifeEventsForm: isCreatingLifeEventsFormReducer,
  createLifeEventsFormError: createLifeEventsFormErrorReducer,
  lifeEventsSelection: lifeEventsSelectionReducer,
  lifeEventsHistory: lifeEventsHistoryReducer,
  lifeEventsHistoryError: lifeEventsHistoryErrorReducer,
  isFinishingLifeEventsForm: isFinishingLifeEventsFormReducer,
  finishLifeEventsFormError: finishLifeEventsFormErrorReducer,
  listLifeEventRemoteSystems: listLifeEventRemoteSystemsReducer,
  isFetchingListLifeEventRemoteSystems: isFetchingListLifeEventRemoteSystemsReducer,
  listLifeEventRemoteSystemsError: listLifeEventRemoteSystemsErrorReducer,
  isPostingSendLifeEventToRemoteSystems: isPostingSendLifeEventToRemoteSystemsReducer,
  sendLifeEventToRemoteSystemsError: sendLifeEventToRemoteSystemsErrorReducer,
  getSelectedLifeEventRemoteSystems: getSelectedLifeEventRemoteSystemsReducer,
  showRemoteSystemsValidationMessage: showRemoteSystemsValidationMessageReducer
});
export default {
  reducers: reducers
};